import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { Previous, Refresh } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  SubmissionLoader,
  AppButton,
  FlexibleSelectDropdown,
  VideoDetails,
  URLDetails,
  SimpleModal,
  InfoNotice,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import {
  MonetizationRequestStatus,
  MonetizationRequestType,
  MonetizationRequestItemStatus,
  ClaimStatus,
} from '@RedflagAI/node-utility/interface';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  initiateFetchMonetizationRequestDetails,
  clearMonetizationRequestDetails,
  initiateUpdateMonetizationRequest,
  fetchCompanyListRequest,
  clearCompanyListRequest,
} from '@Actions';
import {
  NoDataPlaceholder, TableWrapper, tablePadding, renderTableHeader,
} from '../../../Partial/Product/SharedComponents';
import {
  CLAIM_STATUS_OPTIONS,
} from '../../../Partial/Product/ContentId/constants';

const STATUS_OPTIONS = [
  { api: MonetizationRequestStatus.PENDING, display: 'Pending' },
  { api: MonetizationRequestStatus.UNDER_REVIEW, display: 'Under Review' },
  { api: MonetizationRequestStatus.COMPLETED, display: 'Completed' },
];

const CLAIMS_HEADER = [
  { display: 'Claim ID', uid: 'claimId', size: '10%' },
  { display: 'Asset ID', uid: 'assetId', size: '10%' },
  { display: 'Details', uid: 'details', size: '25%' },
  { display: 'Action Required', uid: 'requestedStatus', size: '13%' },
  { display: 'Previous Status', uid: 'previousStatus', size: '13%' },
  { display: 'Review Status', uid: 'reviewedStatus', size: '17%' },
  { display: 'Notes', uid: 'notes', size: '12%' },
];

const ITEM_REVIEW_STATUS_DISPLAY = {
  [MonetizationRequestItemStatus.PENDING]: 'Pending',
  [MonetizationRequestItemStatus.MONETIZED]: 'Monetized',
  [MonetizationRequestItemStatus.TAKENDOWN]: 'Takedown',
  [MonetizationRequestItemStatus.REJECTED]: 'Rejected',
  [MonetizationRequestItemStatus.RELEASED]: 'Released',
};

const AdminMonetizationRequestDetailsPage = ({
  small,
  location,
  loading,
  submitLoading,
  requestDetails,
  fetchRequestDetails,
  clearRequestDetails,
  updateRequestStatus,
  fetchCompanyList,
  clearCompanyList,
  companyList = null,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const [statusValue, setStatusValue] = React.useState('');
  const [statusChanged, setStatusChanged] = React.useState(false);
  const [editingNoteClaim, setEditingNoteClaim] = React.useState(null);
  const [noteDraft, setNoteDraft] = React.useState('');
  const [itemEdits, setItemEdits] = React.useState({});
  const [showPreferencesModal, setShowPreferencesModal] = React.useState(false);
  const [showCompletionModal, setShowCompletionModal] = React.useState(false);
  const [pendingCompletionClaims, setPendingCompletionClaims] = React.useState(null);

  React.useEffect(() => {
    fetchCompanyList();
    fetchRequestDetails(id);
    return () => {
      clearRequestDetails();
      clearCompanyList();
    };
  }, [id]);

  React.useEffect(() => {
    if (requestDetails?.requestStatus) {
      setStatusValue(requestDetails.requestStatus);
      setStatusChanged(false);
    }
  }, [requestDetails]);

  const handleStatusChange = (val) => {
    setStatusValue(val);
    setStatusChanged(val !== requestDetails.requestStatus);
  };

  /* eslint-disable camelcase */
  const handleSaveStatus = () => {
    if (!statusChanged) return;
    updateRequestStatus({
      id: requestDetails.id,
      request_status: statusValue,
    });
    setStatusChanged(false);
  };

  const handleSaveItems = () => {
    if (Object.keys(itemEdits).length === 0) return;
    const claimItems = requestDetails?.items || requestDetails?.claims || [];
    const claims = claimItems.map((claim) => ({
      claim_id: claim.claimId,
      reviewed_status: itemEdits[claim.claimId]?.reviewedStatus
        ?? claim.reviewedStatus
        ?? MonetizationRequestItemStatus.PENDING,
      notes: itemEdits[claim.claimId]?.notes ?? claim.notes ?? '',
    }));
    const allReviewed = claims.every((c) => (
      c.reviewed_status !== MonetizationRequestItemStatus.PENDING
    ));
    if (allReviewed && statusValue !== MonetizationRequestStatus.COMPLETED) {
      // Don't fire the API yet — store claims and let the completion modal
      // send one atomic call (items + COMPLETED) to avoid a race condition
      // where a separate COMPLETED save could land first and block the items update.
      setPendingCompletionClaims(claims);
      setItemEdits({});
      setShowCompletionModal(true);
    } else {
      updateRequestStatus({
        id: requestDetails.id,
        request_status: statusValue,
        claims,
      });
      setItemEdits({});
    }
  };

  const handleConfirmCompletion = () => {
    // Send items + COMPLETED in one call so they're committed atomically.
    updateRequestStatus({
      id: requestDetails.id,
      request_status: MonetizationRequestStatus.COMPLETED,
      claims: pendingCompletionClaims,
    });
    setShowCompletionModal(false);
    setPendingCompletionClaims(null);
    navigate(paths.adminMonetizationRequests);
  };

  const handleCancelCompletion = () => {
    // User chose "Not yet" — save items without marking as completed.
    if (pendingCompletionClaims) {
      updateRequestStatus({
        id: requestDetails.id,
        request_status: statusValue,
        claims: pendingCompletionClaims,
      });
      setPendingCompletionClaims(null);
    }
    setShowCompletionModal(false);
  };
  /* eslint-enable camelcase */

  const handleItemEdit = (claimId, patch) => {
    setItemEdits((prev) => ({
      ...prev,
      [claimId]: { ...prev[claimId], ...patch },
    }));
  };

  const openEditNoteModal = (claim) => {
    setEditingNoteClaim(claim.claimId);
    setNoteDraft(itemEdits[claim.claimId]?.notes ?? claim.notes ?? '');
  };

  const saveNote = () => {
    handleItemEdit(editingNoteClaim, { notes: noteDraft });
    setEditingNoteClaim(null);
    setNoteDraft('');
  };

  const cancelNoteEdit = () => {
    setEditingNoteClaim(null);
    setNoteDraft('');
  };

  const handleRefresh = () => {
    fetchRequestDetails(id);
    fetchCompanyList();
  };

  const handleResetItemEdits = () => {
    setItemEdits({});
  };

  if (loading || !requestDetails) {
    return (
      <AdminNavWrapper
        small={small}
        location={location}
        bannerProps={{
          title: 'Monetization Request Details',
          subTitle: 'Content ID · Admin',
        }}
      >
        <Seo />
        <Box flex align="center" justify="center" pad="large">
          <NoDataPlaceholder
            loading={loading}
            label="Loading details..."
            textColor={page.primaryText}
            loaderColor={page.highlight}
          />
        </Box>
      </AdminNavWrapper>
    );
  }

  const isAutomated = requestDetails.requestType === MonetizationRequestType.AUTOMATED;
  const isRelease = requestDetails.requestType === MonetizationRequestType.RELEASE;

  let requestTypeBadgeBg = 'rgba(0,168,85,0.15)';
  let requestTypeBadgeBorder = '#00a855';
  let requestTypeBadgeColor = '#00a855';
  let requestTypeBadgeLabel = 'Manual Request';
  let requestTypeTitle = 'Manual Monetization Request';
  let requestTypeShort = 'Manual';
  if (isAutomated) {
    requestTypeBadgeBg = 'rgba(240,165,0,0.15)';
    requestTypeBadgeBorder = '#f0a500';
    requestTypeBadgeColor = '#f0a500';
    requestTypeBadgeLabel = 'Automated Request';
    requestTypeTitle = 'Automated Monetization Request';
    requestTypeShort = 'Automated';
  } else if (isRelease) {
    requestTypeBadgeBg = 'rgba(59,130,246,0.15)';
    requestTypeBadgeBorder = '#3b82f6';
    requestTypeBadgeColor = '#3b82f6';
    requestTypeBadgeLabel = 'Release Request';
    requestTypeTitle = 'Claim Release Request';
    requestTypeShort = 'Release';
  }

  const claimsHeader = CLAIMS_HEADER.map((h) => (
    h.uid === 'requestedStatus' && isAutomated
      ? { ...h, display: 'Processing Prefs' }
      : h
  ));

  const renderTableCell = (claim, uid) => {
    const edit = itemEdits[claim.claimId] || {};
    switch (uid) {
      case 'details': {
        // Prefer YouTube video details if present
        if (claim.videoTitle && claim.videoId) {
          return (
            <VideoDetails
              small={small}
              videoTitle={claim.videoTitle}
              videoId={claim.videoId}
              textColor={page.tableText}
              buttonHighlight={page.highlight}
              fallbackHeader="Video details"
            />
          );
        }
        // Fallback to generic details if present
        if (claim.primaryUrl) {
          return (
            <URLDetails
              small={small}
              title={claim.title}
              platform={claim.platform}
              primaryUrl={claim.primaryUrl || claim.primary_url}
              textColor={page.tableText}
              buttonHighlight={page.highlight}
              fallbackHeader="Details"
            />
          );
        }
        return <Text size="1rem" color={page.tableText}>—</Text>;
      }
      case 'reviewedStatus': {
        // Show dropdown for admin to select status
        const currentStatus = edit.reviewedStatus
          ?? claim.reviewedStatus
          ?? MonetizationRequestItemStatus.PENDING;
        const { requestedStatus } = claim;
        // For release requests approve = RELEASED; otherwise MONETIZED or TAKENDOWN
        const nonReleasedApproveApi = requestedStatus === ClaimStatus.TAKEDOWN_REQUESTED
          ? MonetizationRequestItemStatus.TAKENDOWN
          : MonetizationRequestItemStatus.MONETIZED;
        const approveApi = isRelease
          ? MonetizationRequestItemStatus.RELEASED
          : nonReleasedApproveApi;
        const options = [
          { api: MonetizationRequestItemStatus.PENDING, display: 'Pending' },
          { api: approveApi, display: `Approve (${ITEM_REVIEW_STATUS_DISPLAY[approveApi]})` },
          { api: MonetizationRequestItemStatus.REJECTED, display: 'Reject' },
        ];
        return (
          <FlexibleSelectDropdown
            textColor={page.inputText}
            handleItemSelect={(val) => handleItemEdit(claim.claimId, { reviewedStatus: val })}
            flexibleOptions={options}
            noSelectionLabel="Select status"
            displayKey="display"
            valueKey="api"
            customPad="0.2rem 0.3rem"
            selectedOption={options.find((o) => o.api === currentStatus)?.display}
            value={currentStatus}
            withReset={false}
          />
        );
      }
      case 'notes':
        return (
          <Box direction="row" align="center" gap="0.5rem">
            <Text size="1rem" color={page.tableText}>
              {itemEdits[claim.claimId]?.notes ?? claim.notes ?? ''}
            </Text>
            <AppButton
              plain
              level="dynamicSmall"
              label={(itemEdits[claim.claimId]?.notes || claim.notes) ? 'Edit' : 'Add'}
              color={page.highlight}
              onClick={() => openEditNoteModal(claim)}
            />
          </Box>
        );
      case 'requestedStatus': {
        if (isAutomated) {
          return (
            <AppButton
              plain
              level="dynamicSmall"
              label="View prefs"
              color={page.highlight}
              onClick={() => setShowPreferencesModal(true)}
            />
          );
        }
        const actionMap = {
          MONETIZATION_REQUESTED: { label: 'Monetize', color: '#00a855' },
          TAKEDOWN_REQUESTED: { label: 'Takedown', color: '#e53e3e' },
          RELEASE_REQUESTED: { label: 'Release', color: '#3b82f6' },
          REVERT_REQUESTED: { label: 'Revert', color: '#f0a500' },
        };
        const actionCfg = actionMap[claim[uid]];
        if (actionCfg) {
          return (
            <Box
              background={`${actionCfg.color}22`}
              border={{ color: actionCfg.color, size: '1px' }}
              round="6px"
              pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
              width="fit-content"
            >
              <Text size="0.85rem" weight={600} color={actionCfg.color}>
                {actionCfg.label}
              </Text>
            </Box>
          );
        }
        return (
          <Text size="1rem" color={page.tableText}>
            {CLAIM_STATUS_OPTIONS.find((o) => o.api === claim[uid])?.display || claim[uid]}
          </Text>
        );
      }
      case 'previousStatus':
        return (
          <Text size="1rem" color={page.tableText}>
            {CLAIM_STATUS_OPTIONS.find((o) => o.api === claim[uid])?.display || claim[uid]}
          </Text>
        );
      default:
        return (
          <Text size="1rem" color={page.tableText}>
            {claim[uid]}
          </Text>
        );
    }
  };

  const claims = requestDetails.items || requestDetails.claims || [];
  const currentCompany = companyList?.find((c) => parseInt(c.id, 10) === requestDetails?.companyId);

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: `Request #${id}`,
        subTitle: 'Content ID · Admin',
        bannerChildrenPosition: 'bottom',
        bannerChildren: (
          <Box width="fit-content" direction="row" gap="1rem" align="center">
            <Box
              background={requestTypeBadgeBg}
              border={{ color: requestTypeBadgeBorder, size: '1px' }}
              round="6px"
              pad={{ vertical: '0.2rem', horizontal: '0.6rem' }}
            >
              <Text size="0.8rem" weight={600} color={requestTypeBadgeColor}>
                {requestTypeBadgeLabel}
              </Text>
            </Box>
            <AppButton
              overrideHover
              onClick={() => navigate(paths.adminMonetizationRequests)}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
            <AppButton
              overrideHover
              onClick={handleRefresh}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
              disabled={loading || submitLoading}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Updating..."
          contentWidth="20rem"
        />
      )}
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: small ? '1.5rem' : '2rem' }}
        gap="2rem"
        width="100%"
      >
        {isAutomated && (
          <>
            <InfoNotice header="How to review automated requests">
              <Box direction="column" gap="0.75rem">
                <Text size="0.9rem" color={isDarkMode ? page.modalText : 'var(--page-primary-text)'}>
                  This is an automated monetization request. Review the auto-processing preferences
                  below, then check each video in YouTube Studio to determine whether it is a short
                  video, long-form video, or private. Short videos are 3 minutes or less; long-form
                  videos are any video over 3 minutes. Private videos will not be visible to you on
                  YouTube — this is a sign the video is private. Based on those details, select the
                  appropriate review status for each claim in the table, take the corresponding
                  action in YouTube Studio, and add any relevant notes. Once all claims have been
                  handled, set the request status to &quot;Completed&quot; using the controls in
                  the request info section. Don&apos;t forget to click &quot;Save notes &amp;
                  statuses&quot; after making changes.
                </Text>
                {requestDetails.notes && (
                  <Text
                    size="0.9rem"
                    color={isDarkMode ? page.modalText : 'var(--page-primary-text)'}
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {requestDetails.notes}
                  </Text>
                )}
              </Box>
            </InfoNotice>
          </>
        )}
        <Box
          direction={small ? 'column' : 'row'}
          gap="2rem"
          align="start"
          background={page.tableBg}
          pad="1.5rem"
          margin={{ horizontal: small ? '1rem' : '1.5rem' }}
          round="var(--app-radius)"
          border={{ color: page.tableBorder, size: '1px', style: 'solid' }}
        >
          <Box direction="column" width="100%">
            <Box direction="row" gap="1.5rem" justify="start" align="center">
              <Text size="1.25rem" weight={600} color={page.primaryText}>
                {requestTypeTitle}
              </Text>
              <Box direction="row" gap="0.5rem" align="center">
                <FlexibleSelectDropdown
                  textColor={page.inputText}
                  handleItemSelect={handleStatusChange}
                  flexibleOptions={STATUS_OPTIONS}
                  noSelectionLabel="Update status"
                  displayKey="display"
                  valueKey="api"
                  customPad="0.2rem 0.3rem"
                  selectedOption={STATUS_OPTIONS.find((o) => o.api === statusValue)?.display}
                  value={statusValue}
                  withReset={false}
                />
                <AppButton
                  overrideHover
                  disabled={!statusChanged}
                  onClick={handleSaveStatus}
                  level="tableControls"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Save status"
                />
              </Box>
            </Box>
            <Box direction="column" pad={{ vertical: '0.5rem' }} gap="0.5rem">
              <Text size="1rem" color={page.hintText}>
                Instructions:
              </Text>
              {isRelease && (
                <Text size="0.95rem" color={page.primaryText}>
                  Before you begin, update the status of this request to &quot;Under Review&quot;
                  using the controls above. Review each claim below. For each one, go to YouTube
                  Studio and release the claim if appropriate. Set the review status to
                  &quot;Approve (Released)&quot; once you have released the claim in YouTube
                  Studio, or &quot;Reject&quot; if the release cannot be actioned. Add any relevant
                  notes, then click &quot;Save notes &amp; statuses&quot;. Once all claims have
                  been handled, set the request status to &quot;Completed&quot;.
                </Text>
              )}
              {isAutomated && (
                <Text size="0.95rem" color={page.primaryText}>
                  Before you begin, update the status of this request to &quot;Under Review&quot;
                  using the controls above. Check the auto-processing preferences in the notice at
                  the top of this page, then review each claim in the table below. For each video,
                  check the link on YouTube to determine whether it qualifies as a short (3 minutes
                  or less), long-form (over 3 minutes), or private video — private videos will not
                  be visible to you on YouTube, which is a sign the video is private. Select the
                  appropriate review status based on the preferences and take the matching action in
                  YouTube Studio. Add any relevant notes, click &quot;Save notes &amp;
                  statuses&quot;, and once all claims are handled set the status
                  to &quot;Completed&quot;.
                </Text>
              )}
              {!isRelease && !isAutomated && (
                <>
                  <Text size="0.95rem" color={page.primaryText}>
                    Before you begin reviewing the claims below, please make sure
                    to update the status of this request to &quot;Under Review&quot; using
                    the controls above. Review each item in the table below, select a status
                    based on what action you took in YouTube Studio for the corresponding claim,
                    and add any relevant notes. Note that short videos are 3 minutes or less and
                    long-form videos are any video over 3 minutes. Private videos will not be
                    visible to you on YouTube — this is a sign the video is private. Once
                    you&apos;ve completed your review and all claims have been updated on YouTube
                    Studio, you can set the status to &quot;Completed&quot;.
                  </Text>
                  <Text size="0.95rem" color={page.primaryText}>
                    Don&apos;t forget to click &quot;Save changes&quot;
                    after updating the status or adding/editing notes
                    to ensure your changes are saved.
                  </Text>
                </>
              )}
            </Box>
            <Box direction="row" gap="1.5rem" wrap>
              <Box pad={{ right: '1rem', vertical: '1rem' }}>
                <Text color={page.hintText} size="0.95rem">Request ID:</Text>
                <Text color={page.primaryText} size="1rem" weight={500}>{requestDetails.requestId || requestDetails.id}</Text>
              </Box>
              <Box pad={{ right: '1rem', vertical: '1rem' }}>
                <Text color={page.hintText} size="0.95rem">Request Type:</Text>
                <Text color={page.primaryText} size="1rem" weight={500}>
                  {requestTypeShort}
                </Text>
              </Box>
              <Box pad={{ right: '1rem', vertical: '1rem' }}>
                <Text color={page.hintText} size="0.95rem">Company Name:</Text>
                <Text color={page.primaryText} size="1rem" weight={500}>
                  {currentCompany?.name || 'N/A'}
                </Text>
              </Box>
              <Box pad={{ right: '1rem', vertical: '1rem' }}>
                <Text color={page.hintText} size="0.95rem">Company ID:</Text>
                <Text color={page.primaryText} size="1rem" weight={500}>{requestDetails.companyId}</Text>
              </Box>
              {requestDetails.requestedByUserId && (
                <Box pad={{ right: '1rem', vertical: '1rem' }}>
                  <Text color={page.hintText} size="0.95rem">Requested By User ID:</Text>
                  <Text color={page.primaryText} size="1rem">{requestDetails.requestedByUserId}</Text>
                </Box>
              )}
              {requestDetails.requestedByEmail && (
                <Box pad={{ right: '1rem', vertical: '1rem' }}>
                  <Text color={page.hintText} size="0.95rem">Requested By Email:</Text>
                  <Text color={page.primaryText} size="1rem">{requestDetails.requestedByEmail}</Text>
                </Box>
              )}
              {requestDetails.completedByUserId && (
                <Box pad={{ right: '1rem', vertical: '1rem' }}>
                  <Text color={page.hintText} size="0.95rem">Completed By User ID:</Text>
                  <Text color={page.primaryText} size="1rem">{requestDetails.completedByUserId}</Text>
                </Box>
              )}
              {requestDetails.completedByEmail && (
                <Box pad={{ right: '1rem', vertical: '1rem' }}>
                  <Text color={page.hintText} size="0.95rem">Completed By Email:</Text>
                  <Text color={page.primaryText} size="1rem">{requestDetails.completedByEmail}</Text>
                </Box>
              )}
              {requestDetails.completedAt && (
                <Box pad={{ right: '1rem', vertical: '1rem' }}>
                  <Text color={page.hintText} size="0.95rem">Completed At:</Text>
                  <Text color={page.primaryText} size="1rem">{buildDateTimeLabel(requestDetails.completedAt)}</Text>
                </Box>
              )}
              <Box pad={{ right: '1rem', vertical: '1rem' }}>
                <Text color={page.hintText} size="0.95rem">Created:</Text>
                <Text color={page.primaryText} size="1rem">{buildDateTimeLabel(requestDetails.createdAt)}</Text>
              </Box>
              <Box pad={{ right: '1rem', vertical: '1rem' }}>
                <Text color={page.hintText} size="0.95rem">Updated:</Text>
                <Text color={page.primaryText} size="1rem">{buildDateTimeLabel(requestDetails.updatedAt)}</Text>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box width="100%">
          <TableWrapper
            small={small}
            header="Claims for processing"
            boxPad={tablePadding(small)}
            isPaged={false}
            totalResults={claims.length}
            currentResults={claims.length}
            selectedResultsCount={claims.length}
            tableControls={(
              <Box direction="row" gap="0.5rem">
                <AppButton
                  overrideHover
                  disabled={Object.keys(itemEdits).length === 0}
                  onClick={handleSaveItems}
                  level="tableControls"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Save notes & statuses"
                />
                <AppButton
                  overrideHover
                  disabled={Object.keys(itemEdits).length === 0}
                  onClick={handleResetItemEdits}
                  level="tableControls"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Reset"
                />
              </Box>
            )}
            currentPage={0}
            handlePage={() => {}}
            hidePagination
          >
            <Box fill="horizontal" direction="column">
              {!small && (
                <Box
                  fill="horizontal"
                  wrap={false}
                  direction="row"
                  border={{
                    color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
                  }}
                >
                  {claimsHeader.map(({ uid, display, size }) => (
                    <Box
                      key={uid}
                      width={size}
                      pad={{
                        horizontal: '0.5rem',
                        top: '1rem',
                        bottom: '0.5rem',
                      }}
                    >
                      {renderTableHeader(display, small)}
                    </Box>
                  ))}
                </Box>
              )}
              {claims.map((claim) => (
                <Box
                  key={claim.claimId}
                  direction={small ? 'column' : 'row'}
                  align={small ? 'start' : 'center'}
                  border={{
                    color: page.tableBorder,
                    size: '1px',
                    style: 'solid',
                    side: 'bottom',
                  }}
                  pad={{ vertical: '1rem', right: '0.5rem' }}
                  background={page.tableBg}
                  fill="horizontal"
                >
                  {claimsHeader.map(({ uid, display, size }) => (
                    <Box
                      key={uid}
                      width={small ? '100%' : size}
                      pad={{
                        horizontal: small ? '0' : '0.5rem',
                        vertical: small ? '0.25rem' : '0',
                      }}
                      direction="row"
                      gap="0.5rem"
                    >
                      {small && (
                        <Text size="0.875rem" color={page.tableHeader}>
                          {display}
                          :
                        </Text>
                      )}
                      {renderTableCell(claim, uid)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </TableWrapper>
        </Box>
      </Box>
      {editingNoteClaim && (
        <SimpleModal
          small={small}
          modalTitle="Edit Notes"
          toggleModal={cancelNoteEdit}
          hideLowerSection
          modalBg={page.modalBg}
          textColor={page.modalText}
          highlightColor={page.highlight}
          titleSize="1.2rem"
          desktopMinWidth="30rem"
        >
          <Box gap="1rem">
            <textarea
              style={{
                minHeight: '6rem',
                borderRadius: '6px',
                border: `1px solid ${page.inputBorder}`,
                padding: '0.75rem',
                fontSize: '1rem',
                color: page.inputText,
                background: page.inputBg,
              }}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Enter any notes for this claim"
            />
            <Box direction="row" gap="1rem" justify="end">
              <AppButton
                overrideHover
                onClick={cancelNoteEdit}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Cancel"
              />
              <AppButton
                overrideHover
                onClick={saveNote}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Save"
                disabled={noteDraft === (claims.find((c) => c.claimId === editingNoteClaim)?.notes ?? '')}
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
      {showCompletionModal && (
        <SimpleModal
          noClickOutside
          small={small}
          modalTitle="All items reviewed"
          toggleModal={handleCancelCompletion}
          hideLowerSection
          modalBg={page.modalBg}
          textColor={page.modalText}
          highlightColor={page.highlight}
          titleSize="1.2rem"
          desktopMinWidth="30rem"
        >
          <Box gap="1rem">
            <Text size="1rem" color={page.modalText}>
              All items in this request have been reviewed. Would you like to mark this
              request as completed and return to the request list page?
            </Text>
            <Box direction="row" gap="1rem" justify="end">
              <AppButton
                overrideHover
                onClick={handleCancelCompletion}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Not yet"
              />
              <AppButton
                overrideHover
                onClick={handleConfirmCompletion}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                fontWeight={600}
                label="Confirm completion"
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
      {showPreferencesModal && (
        <SimpleModal
          small={small}
          modalTitle="Company-level Preferences"
          toggleModal={() => setShowPreferencesModal(false)}
          hideLowerSection
          modalBg={page.modalBg}
          textColor={page.modalText}
          highlightColor={page.highlight}
          titleSize="1.2rem"
          desktopMinWidth="30rem"
        >
          <Box gap="1rem">
            <Text
              size="1rem"
              color={page.modalText}
              style={{ whiteSpace: 'pre-line' }}
            >
              {requestDetails.notes || 'Check company CID feature settings to view the auto-processing preferences for this request.'}
            </Text>
          </Box>
        </SimpleModal>
      )}
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    requestDetails: state.adminMonetizationRequests.details,
    companyList: state.adminCompany.companies,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchRequestDetails: initiateFetchMonetizationRequestDetails,
    clearRequestDetails: clearMonetizationRequestDetails,
    updateRequestStatus: initiateUpdateMonetizationRequest,
    fetchCompanyList: fetchCompanyListRequest,
    clearCompanyList: clearCompanyListRequest,
  }, dispatch);
}

AdminMonetizationRequestDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchRequestDetails: PropTypes.func.isRequired,
  clearRequestDetails: PropTypes.func.isRequired,
  updateRequestStatus: PropTypes.func.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  clearCompanyList: PropTypes.func.isRequired,
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  requestDetails: PropTypes.shape({
    id: PropTypes.string.isRequired,
    requestId: PropTypes.string,
    requestType: PropTypes.string,
    notes: PropTypes.string,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    requestedByUserId: PropTypes.string,
    requestedByEmail: PropTypes.string,
    completedByUserId: PropTypes.string,
    completedByEmail: PropTypes.string,
    completedAt: PropTypes.string,
    requestStatus: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      claimId: PropTypes.string.isRequired,
      requestedStatus: PropTypes.string.isRequired,
      previousStatus: PropTypes.string.isRequired,
      assetId: PropTypes.string.isRequired,
      videoTitle: PropTypes.string.isRequired,
      videoId: PropTypes.string.isRequired,
      videoUrl: PropTypes.string.isRequired,
      notes: PropTypes.string,
    })),
    claims: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminMonetizationRequestDetailsPage);
